import React, { useState, useMemo } from 'react';
import { ChevronLeft } from 'lucide-react';
import PrimaryNavigation from 'components/ui/PrimaryNavigation';
import SAMetricCards from './components/SAMetricCards';
import SAIntentSection from './components/SAIntentSection';
import SAChatList from './components/SAChatList';
import SAChatView from './components/SAChatView';
import SADateFilter from './components/SADateFilter';

/** Convert the SADateFilter value into { from, to } ISO datetime strings (YYYY-MM-DDTHH:mm:ss). */
function computeDateRange(value) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(startOfToday);
  endOfToday.setDate(endOfToday.getDate() + 1);

  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}:${ss}`;
  };

  switch (value) {
    case 'Today':
      return { from: fmt(startOfToday), to: fmt(endOfToday) };
    case 'Yesterday': {
      const y = new Date(startOfToday);
      y.setDate(y.getDate() - 1);
      return { from: fmt(y), to: fmt(startOfToday) };
    }
    case 'Last 7 Days': {
      const d = new Date(startOfToday);
      d.setDate(d.getDate() - 7);
      return { from: fmt(d), to: fmt(endOfToday) };
    }
    case 'Last 30 Days': {
      const d = new Date(startOfToday);
      d.setDate(d.getDate() - 30);
      return { from: fmt(d), to: fmt(endOfToday) };
    }
    case 'Last Month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: fmt(first), to: fmt(last) };
    }
    case 'All Time':
      return { from: '2020-01-01T00:00:00', to: fmt(endOfToday) };
    default:
      // Custom range: "2025-05-01 → 2025-05-28"
      if (value?.includes('→')) {
        const [s, e] = value.split('→').map((s) => s.trim());
        const end = new Date(e);
        end.setDate(end.getDate() + 1);
        return { from: `${s}T00:00:00`, to: fmt(end) };
      }
      return { from: '2020-01-01T00:00:00', to: fmt(endOfToday) };
  }
}

const ShoppingAssistance = () => {
  const [dateRange, setDateRange] = useState('Last 7 Days');
  const [selectedChat, setSelectedChat] = useState(null);
  const [mobileChatListOpen, setMobileChatListOpen] = useState(true);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const handleChatUpdate = (updatedChat) => {
    setSelectedChat(updatedChat);
    setListRefreshKey((k) => k + 1);
  };

  const { from, to } = useMemo(() => computeDateRange(dateRange), [dateRange]);

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content flex flex-col bg-gray-50 min-h-screen overflow-y-auto">

        {/* Top Bar */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between px-4 md:px-6 py-4 bg-white border-b border-gray-100 shrink-0">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Shopping Assistance</h1>
            <p className="text-xs text-gray-500 mt-0.5">Monitor eCommerce chat conversations in real time</p>
          </div>
          <SADateFilter value={dateRange} onChange={setDateRange} />
        </div>

        {/* Summary + Intents */}
        <div className="px-4 md:px-6 py-4 space-y-3 shrink-0 bg-white border-b border-gray-100">
          <SAMetricCards from={from} to={to} />
          <SAIntentSection from={from} to={to} />
        </div>

        {/* Chat Inbox */}
        <div className="flex flex-1 relative h-[400px] md:h-[680px] overflow-hidden">
          {/* Left: Chat List */}
          <div className={`${mobileChatListOpen ? 'flex' : 'hidden'} lg:flex w-full lg:w-72 shrink-0 flex-col border-r border-gray-100 bg-white absolute lg:relative inset-0 z-10 lg:z-auto`}>
            <SAChatList
              from={from}
              to={to}
              selectedId={selectedChat?.id}
              refreshKey={listRefreshKey}
              onSelect={(chat) => {
                setSelectedChat(chat);
                setMobileChatListOpen(false);
              }}
            />
          </div>

          {/* Middle: Chat View */}
          <div className={`${!mobileChatListOpen ? 'flex' : 'hidden'} lg:flex flex-1 flex-col min-w-0`}>
            {/* Mobile back button */}
            <button
              onClick={() => { setMobileChatListOpen(true); setSelectedChat(null); }}
              className="lg:hidden flex items-center gap-1 px-4 py-2.5 text-sm text-blue-600 font-medium bg-white border-b border-gray-100"
            >
              <ChevronLeft size={16} /> Back to conversations
            </button>
            <SAChatView chat={selectedChat} onChatUpdate={handleChatUpdate} />
          </div>
        </div>
      </div>
    </>
  );
};

export default ShoppingAssistance;
